import type React from 'react';
import { useEffect, useState } from 'react';

import { CircularProgress, List as MuiList, Stack, Typography } from '@mui/material';
import { componentLogger } from '@ragu-components/logger';
import {
  OrdBottomToolbar,
  OrdFormList,
  OrdIconStandard,
  OrdSimpleForm,
  useFormType,
} from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import {
  BooleanInput,
  useDelete,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
  useTranslate,
} from 'react-admin';

import { FilePickerField } from 'src/components/FilePickerField';
import { useIngordDataProvider } from 'src/components/flowchart/hooks/useIngordDataProvider';
import { uploadFile } from 'src/lib/tus';

import { getErrorMessage } from './utils';

import type { OrdSimpleFormProps } from '@ragu-components/ord-components';
import type { RunningTask } from '@ragu-components/ord-types';

export const UserImportForm = (props: OrdSimpleFormProps): React.ReactElement => {
  const translate = useTranslate();
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
  const [fileError, setFileError] = useState<string | null>();
  const record = useRecordContext<RunningTask>();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const recordId = record != null ? record.id : undefined;
  const confirmTitle = `${translate(`resources.user_import.operations.delete`)} ${recordId ?? ''}?`;
  const [deleteOne] = useDelete();
  const { create, edit } = useFormType();

  const [isFileLoading, setIsFileLoading] = useState(edit);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const forceSaveButtonEnabled = !isSubmitting && selectedFile != null;
  const forceSaveButtonDisabled = isSubmitting || (create && selectedFile == null);
  const dataProvider = useIngordDataProvider();

  useEffect(() => {
    const fetchAudio = async (): Promise<void> => {
      if (recordId !== undefined) {
        try {
          setIsFileLoading(true);
          const { data, fileType } = await dataProvider.downloadFile({
            resource: Resources.tasks,
            resource_id: recordId,
          });
          const uint8Array = new Uint8Array(data);
          const blob = new Blob([uint8Array], { type: fileType });
          const fileExtension = fileType.split('/')[1];
          const file = new File([blob], `${recordId}.${fileExtension}`, { type: fileType });
          if (data != null) {
            setSelectedFile(file);
          }
        } catch (error) {
          componentLogger.error('Error fetching user import file:', error);
        } finally {
          setIsFileLoading(false);
        }
      }
    };
    if (edit) {
      void fetchAudio();
    }
  }, [dataProvider, edit, recordId]);

  const onFileSelected = (file: File | undefined): void => {
    setSelectedFile(file);
  };

  const onFileRemoved = (): void => {
    setSelectedFile(undefined);
  };

  const onFileRejected = (rejected: boolean): void => {
    setFileError(rejected ? 'resources.user_import.errors.fileInvalid' : null);
  };

  const rollback = async (id: string): Promise<void> => {
    if (create) {
      return deleteOne('tasks', { id });
    }
  };

  const onSuccess = async ({ id }: RunningTask): Promise<void> => {
    if (selectedFile != null) {
      await uploadFile(selectedFile, id, 'tasks')
        .then(async () => {
          const message = `resources.user_import.notifications.created`;
          notify(message, { messageArgs: { smart_count: 1 } });
          redirect('list', 'tasks');
          refresh();
        })
        .catch(async (error: unknown | number) => {
          setFileError(getErrorMessage(error));
          await rollback(id);
        })
        .finally(() => {
          setIsSubmitting(false);
          setSelectedFile(undefined);
        });
    }
  };

  const LoadingProgress = (): React.ReactElement => {
    return (
      <Stack width='100%' height='100%' direction={'row'} justifyContent={'center'} alignItems={'center'}>
        <CircularProgress />
      </Stack>
    );
  };

  return (
    <OrdSimpleForm
      sanitizeFields
      toolbar={
        <OrdBottomToolbar
          saveProps={{
            onClick: () => {
              setIsSubmitting(true);
            },
            type: 'button',
            mutationOptions: { onSuccess },
            alwaysEnable: forceSaveButtonEnabled,
            disabled: forceSaveButtonDisabled,
          }}
          deleteProps={{ confirmTitle, record: { id: recordId } }}
          strictDelete={false}
        />
      }
      {...props}
    >
      {isFileLoading ? (
        <LoadingProgress />
      ) : (
        <MuiList sx={{ width: '100%' }}>
          <OrdFormList icon={<OrdIconStandard name='fileUpload' />}>
            <>
              <FilePickerField
                accept={{ 'text/csv': ['.csv'] }}
                file={selectedFile}
                hasError={Boolean(fileError)}
                onFileSelected={onFileSelected}
                onFileRejected={onFileRejected}
                onClear={onFileRemoved}
              />
              {fileError != null && (
                <Typography variant='caption' color='error'>
                  {translate(fileError)}
                </Typography>
              )}
            </>
            <BooleanInput
              source='overwrite'
              label={translate('resources.user_import.fields.overwrite')}
              defaultValue={false}
            />
          </OrdFormList>
        </MuiList>
      )}
    </OrdSimpleForm>
  );
};
